import axios, { RawAxiosRequestHeaders } from "axios";

import type {
  DeletePaths,
  DeleteRequest,
  GetPaths,
  GetRequest,
  GetResult,
  PostPaths,
  PostRequest,
  PostResult,
  PutPaths,
  PutRequest,
  RawGetResult,
  RawPostResult,
  RawPutResult,
} from "./types";

export const getApiBaseUrl = () => {
  return (
    localStorage.getItem("baseApiUrl") ||
    import.meta.env.REACT_APP_API_BASE_URL ||
    ""
  );
};

/**
 * Substitutes all path variables from provided args, and return the resulting
 * path and remaining args
 */
const derefPath = (p: string, args: Record<string, unknown> = {}) =>
  Object.entries(args).reduce<[string, Record<string, unknown>]>(
    ([path, rest], [k, v]) =>
      path.includes(`{${k}}`)
        ? [path.replace(`{${k}}`, `${v}`), rest]
        : [path, { ...rest, [k]: v }],
    [p, {}]
  );

/**
 * Perform a `GET` request against a known patient app resource
 */
export const get = async <Path extends GetPaths>(
  path: Path,
  ...[args]: GetRequest<Path> extends undefined ? [] : [GetRequest<Path>]
) => {
  const baseUrl = getApiBaseUrl();
  const [resolvedPath, params] = derefPath(
    `${baseUrl}/api/v1${path}`,
    args ?? {}
  );

  const { data: response, status } = await axios.get<RawGetResult<Path>>(
    resolvedPath,
    {
      headers: { "Cache-Control": "no-cache" },
      params,
    }
  );

  if ("result" in response && response.result != null) {
    return response.result as GetResult<Path>;
  } else if (!("result" in response) && status === 200) {
    return response as GetResult<Path>;
  } else {
    console.log(response);
    return Promise.reject(`Failed to load api get result for ${path}`);
  }
};

/**
 * Perform a `GET` request against a known patient app resource. Prefer using
 * `get` instead of this function. This function is useful to pass custom headers.
 */
export const rawGet = async <Path extends GetPaths>(
  path: Path,
  args: GetRequest<Path>,
  headers?: RawAxiosRequestHeaders
): Promise<GetResult<Path>> => {
  const baseUrl = getApiBaseUrl();
  const [resolvedPath, params] = derefPath(
    `${baseUrl}/api/v1${path}`,
    args ?? {}
  );

  const { data, status } = await axios.get<RawGetResult<Path>>(resolvedPath, {
    params,
    headers,
  });

  if ("result" in data && data.result != null) {
    return data.result as GetResult<Path>;
  } else if (!("result" in data) && status === 200) {
    return data as GetResult<Path>;
  } else {
    return Promise.reject(`Failed to load api get result for ${path}`);
  }
};

/**
 * Perform a `POST` request against a known patient app resource
 */
export const post = async <Path extends PostPaths>(
  path: Path,
  args: PostRequest<Path>,
  token?: string | null | undefined
) => {
  const baseUrl = getApiBaseUrl();
  const [resolvedPath, params] = derefPath(
    `${baseUrl}/api/v1${path}`,
    args ?? {}
  );

  const { data: response, status } = await axios.post<RawPostResult<Path>>(
    resolvedPath,
    "$data" in params ? params.$data : params,
    {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  const successStatusCodes: number[] = [200, 201, 204];

  // temporary fix - some endpoints return do not return a { result } object
  if (successStatusCodes.includes(status) && typeof response === "string") {
    return response as PostResult<Path>;
  } else if (
    successStatusCodes.includes(status) &&
    typeof response === "object" &&
    "result" in response
  ) {
    return response.result as PostResult<Path>;
  } else {
    return Promise.reject(`Failed to load api post result for ${path}`);
  }
};

/**
 * Perform a `PUT` request against a known patient app resource
 */
export const put = async <Path extends PutPaths>(
  path: Path,
  args: PutRequest<Path>
) => {
  const baseUrl = getApiBaseUrl();
  const [resolvedPath, data] = derefPath(
    `${baseUrl}/api/v1${path}`,
    args ?? {}
  );

  const { data: response } = await axios.put<RawPutResult<Path>>(
    resolvedPath,
    Array.isArray(data.$data) ? data.$data : data,
    { headers: { "Content-Type": "application/json" } }
  );

  if (response.result !== undefined) {
    return response.result as PutRequest<Path>;
  } else {
    return Promise.reject(`Failed to load api put result for ${path}`);
  }
};

/**
 * Perform a `DELETE` request against a known patient app resource
 */
export const del = async <Path extends DeletePaths>(
  path: Path,
  args: DeleteRequest<Path>
) => {
  const baseUrl = getApiBaseUrl();
  const [resolvedPath, data] = derefPath(
    `${baseUrl}/api/v1${path}`,
    args ?? {}
  );

  await axios.delete(resolvedPath, {
    headers: { "Content-Type": "application/json" },
    data: "$data" in data ? data.$data : data,
  });
};

export const pget = <P extends GetPaths>(path: P) => {
  type Args = GetRequest<P> extends undefined ? [] : [GetRequest<P>];

  // @ts-expect-error we're summersaulting around the type system here
  return Object.assign((...[args]: Args) => get(path, args), {
    key: (...[args]: Args) => [path, args],
    keyed: (...[args]: Args) => ({
      queryKey: [path, args],
      // @ts-expect-error see above
      queryFn: () => get(path, args),
    }),
  });
};

export const pput = <P extends PutPaths>(path: P) => {
  return (data: Parameters<typeof put<P>>[1]) => put(path, data);
};

export const ppost = <P extends PostPaths>(path: P) => {
  return (data: Parameters<typeof post<P>>[1]) => post(path, data);
};

export const pdel = <P extends DeletePaths>(path: P) => {
  return (data: Parameters<typeof del<P>>[1]) => del(path, data);
};

export type { GetResult, PostResult, PutRequest, DeleteRequest };
